import { memo, useContext, useRef } from 'react';

import { SubstanceLogColors } from '@/constants/color';
import PersonContext from '@/pages/Person/PersonContext';
import { LastLogsResult } from '@/types/substanceSummary';
import Card from '@/ui/components/Card';
import Heading from '@/ui/components/Heading';
import { isToday, parseIsoDate } from '@/utils/dates';

interface Props {
  logs: LastLogsResult[];
  index: number;
}

const SubstanceTrackerLogs = ({ logs, index }: Props) => {
  const { person } = useContext(PersonContext);
  const daysLogged = useRef(0);
  daysLogged.current = 0;

  const substanceInputValueBackgroundColor: { [key: number]: string } = {
    0: SubstanceLogColors.GREEN_600,
    1: SubstanceLogColors.YELLOW_600,
    2: SubstanceLogColors.ORANGE_600,
    3: SubstanceLogColors.RED_600,
    4: SubstanceLogColors.RED_700
  };

  const getBackgroundColor = (inputValue: number | null) => {
    if (inputValue === null) return SubstanceLogColors.GRAY_500;
    return substanceInputValueBackgroundColor[inputValue] ?? SubstanceLogColors.RED_800;
  };

  const renderLogsByWeek = (title: string, days: LastLogsResult[]) => {
    const reversedDays = days.slice().reverse();
    return (
      <div className="flex flex-col h-full justify-between mt-4">
        <Heading value={4} text={title} />
        <div className="flex flex-wrap justify-start items-start mt-2">
          {reversedDays.map((item, idx) => {
            return (
              <div
                key={idx}
                className="grow p-2"
                style={{ minWidth: '50px', maxWidth: 'calc(100% / 7)' }}>
                {renderLogs(item, index, idx)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLogs = (item: LastLogsResult, index: number, idx: number) => {
    let lastDate = '';
    const dateStr = item.logged_on;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    const formattedDay = String(date.getUTCDate()).padStart(2, '0');
    const formattedMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
    lastDate = `${formattedMonth}/${formattedDay}`;

    const parseDate = parseIsoDate(item.logged_on);
    if (person.time_zone && isToday(parseDate, person.time_zone)) {
      lastDate = 'Today';
    }

    return (
      <div key={`${index}-${idx}`} className="flex flex-col items-center mx-2">
        <div
          className="flex items-center justify-center w-9 h-9 text-white font-bold rounded-full"
          style={{
            backgroundColor: getBackgroundColor(item.uses)
          }}>
          {item.uses ?? '?'}
        </div>
        <p className="text-center text-xs mt-1" style={{ minHeight: '1.5em' }}>
          {lastDate}
        </p>
      </div>
    );
  };

  return (
    <Card key={`${index}-uses`} className="flex-1">
      <div className="flex flex-col h-full justify-between">
        <Heading value={2} text={'Substance Tracker'} />
        <div>
          {renderLogsByWeek('Last 7 days', logs.slice(0, 7))}
          {renderLogsByWeek('Last 7 to 14 days', logs.slice(7, 14))}
        </div>
      </div>
    </Card>
  );
};

export default memo(SubstanceTrackerLogs);
